const PAGE_TO_PATH = {
  dashboard: '/app/dashboard',
  targets: '/app/targets',
  scans: '/app/scans',
  activeScans: '/app/active-scans',
  // ocva: '/app/ocva',
  earlyDisclosures: '/app/early-disclosures',
  earlyDisclosures2: '/app/early-disclosures/ghost-cve-exposures',
  risks: '/app/risks',
  reports: '/app/reports',
  // aiIntelligence: '/app/ai-intelligence',
  securityEvents: '/app/security-events',
  // xdr: '/app/xdr',
  alerts: '/app/alerts',
  subscription: '/app/subscription',
  subscriptionOrgDetail: '/app/subscription/plan-usage',
  support: '/app/support',
  profileSettings: '/app/settings/profile',
  settingsConnectors: '/app/settings/connectors',
  settingsDeployment: '/app/settings/deployment',
  settingsManageUsers: '/app/settings/users',
  settingsUsersOrgs: '/app/settings/organizations-users',
  settingsSites: '/app/settings/sites',
  settingsScanNetwork: '/app/settings/scan-network',
  settingsScanNetworkOps: '/app/settings/scan-network-ops',
  settingsPartners: '/app/settings/partners',
};

const PATH_TO_PAGE = Object.entries(PAGE_TO_PATH).reduce((acc, [page, path]) => {
  acc[path] = page;
  return acc;
}, {});

export const DASHBOARD_PAGE_PATHS = Object.freeze({ ...PAGE_TO_PATH });

export const DASHBOARD_PAGE_DEFAULT = 'dashboard';

export const APP_SETTINGS_PAGES = [
  'profileSettings',
  'settingsConnectors',
  'settingsDeployment',
  'settingsManageUsers',
  'settingsUsersOrgs',
  'settingsSites',
  'settingsScanNetwork',
  'settingsScanNetworkOps',
  'settingsPartners',
];

export function normalizePageKey(page) {
  const key = String(page || '').trim();
  if (key === 'riskDetail') return 'riskDetail';
  if (key === 'scanDetail') return 'scanDetail';
  // Legacy Activities page — folded into Scans list filters.
  if (key === 'scanActivity') return 'scans';
  return Object.prototype.hasOwnProperty.call(PAGE_TO_PATH, key) ? key : DASHBOARD_PAGE_DEFAULT;
}

export function isAppSettingsPage(page) {
  return APP_SETTINGS_PAGES.includes(normalizePageKey(page));
}

/** List pages whose data table should fill leftover viewport height (Risks pattern). */
export const TABLE_FILL_PAGES = [
  'targets',
  'scans',
  'activeScans',
  'risks',
  'reports',
  'earlyDisclosures',
  'earlyDisclosures2',
  'settingsConnectors',
  'settingsManageUsers',
  'settingsUsersOrgs',
  'settingsSites',
  'settingsPartners',
  'subscription',
];

export function isTableFillPage(page) {
  return TABLE_FILL_PAGES.includes(normalizePageKey(page));
}

export function pageToPath(page) {
  const key = normalizePageKey(page);
  return PAGE_TO_PATH[key] || PAGE_TO_PATH[DASHBOARD_PAGE_DEFAULT];
}

export function pathToPage(pathname) {
  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';
  if (normalized === '/app') return DASHBOARD_PAGE_DEFAULT;
  if (/^\/app\/risks\/[^/]+$/.test(normalized)) return 'riskDetail';
  // Legacy Activities URL — treat as Scans (callers should redirect to /app/scans).
  if (normalized === '/app/scans/activity') return 'scans';
  if (/^\/app\/scans\/[^/]+$/.test(normalized)) return 'scanDetail';
  if (/^\/app\/reports\/[^/]+$/.test(normalized)) return 'reports';
  if (normalized === '/dashboard/reports' || /^\/dashboard\/reports\/[^/]+$/.test(normalized)) return 'reports';
  return PATH_TO_PAGE[normalized] || null;
}

export function riskDetailPath(riskId, scannerType = '') {
  const id = encodeURIComponent(String(riskId || '').trim());
  const scanner = String(scannerType || '').trim().toLowerCase();
  const qs = scanner ? `?scanner=${encodeURIComponent(scanner)}` : '';
  return `/app/risks/${id}${qs}`;
}

export function scanDetailPath(scanId, scanType = 'web', networkLocation = null) {
  const id = encodeURIComponent(String(scanId || '').trim());
  const type = String(scanType || '').trim().toLowerCase() === 'network' ? 'network' : 'web';
  const params = new URLSearchParams();
  params.set('type', type);
  // Preserve on-prem vs perimeter so sidebar highlight stays correct on detail.
  const location = String(networkLocation || '').trim().toLowerCase();
  if (type === 'network' && (location === 'perimeter' || location === 'onprem')) {
    params.set('location', location);
  }
  return `/app/scans/${id}?${params.toString()}`;
}

function normalizeRisksDateParam(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  // Accept YYYY-MM-DD or DD-MM-YYYY from trend chart labels.
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return '';
}

const RISKS_RANGE_TOKENS = new Set(['7d', '30d', '90d', '6m', '1y', 'all']);

function normalizeRisksRangeParam(value) {
  const token = String(value || '').trim().toLowerCase();
  if (!token || token === 'all' || token === 'all_time') return '';
  return RISKS_RANGE_TOKENS.has(token) ? token : '';
}

/** Build `/app/risks` with optional list filters (survives per-route DashboardPage remounts). */
export function buildRisksPathWithFilters(filters = {}) {
  const params = new URLSearchParams();
  const severity = String(filters?.severity || '').trim();
  const scanner = String(filters?.scanner || '').trim().toLowerCase();
  // Default open only when status is omitted. Explicit '' / 'all' means All Statuses.
  const hasStatus = filters != null && Object.prototype.hasOwnProperty.call(filters, 'status');
  const statusRaw = hasStatus ? String(filters.status ?? '').trim().toLowerCase() : 'open';
  const status = statusRaw === 'all' ? '' : statusRaw;
  const q = String(filters?.q || filters?.search || '').trim();
  const date = normalizeRisksDateParam(filters?.date);
  const range = normalizeRisksRangeParam(filters?.range);
  if (severity) params.set('severity', severity);
  if (scanner) params.set('scanner', scanner);
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  // Prefer a single-day date (trend click); otherwise pass rolling overview range.
  if (date) params.set('date', date);
  else if (range) params.set('range', range);
  const qs = params.toString();
  return `${PAGE_TO_PATH.risks}${qs ? `?${qs}` : ''}`;
}

export function parseRisksFiltersFromSearchParams(searchParams) {
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(String(searchParams || ''));
  return {
    severity: sp.get('severity') || '',
    scanner: (sp.get('scanner') || '').trim().toLowerCase(),
    status: (sp.get('status') || '').trim().toLowerCase(),
    q: String(sp.get('q') || sp.get('search') || '').trim(),
    date: normalizeRisksDateParam(sp.get('date')),
    range: normalizeRisksRangeParam(sp.get('range')),
  };
}

const SCANS_STATUS_FILTERS = new Set(['all', 'in_progress', 'scheduled', 'completed', 'failed']);
const SCANS_RECORD_VIEWS = new Set(['runs', 'in_progress', 'schedules']);
const SCANS_LOCATIONS = new Set(['all', 'onprem', 'perimeter', 'scheduled', 'current', 'in_progress']);
const ACTIVE_SCANS_STATUS_FILTERS = new Set(['in_progress', 'scheduled']);
const ACTIVE_SCANS_TYPE_FILTERS = new Set(['all', 'web', 'onprem', 'perimeter']);

/** Build `/app/active-scans` for the header-only combined Web+Network active list. */
export function buildActiveScansPath({ status = 'in_progress', type = 'all' } = {}) {
  const params = new URLSearchParams();
  const safeStatus = String(status || 'in_progress').trim().toLowerCase();
  const safeType = String(type || 'all').trim().toLowerCase();
  params.set(
    'status',
    ACTIVE_SCANS_STATUS_FILTERS.has(safeStatus) ? safeStatus : 'in_progress'
  );
  if (safeType === 'web' || safeType === 'onprem' || safeType === 'perimeter') {
    params.set('type', safeType);
  }
  return `${PAGE_TO_PATH.activeScans}?${params.toString()}`;
}

export function parseActiveScansStatusFromSearchParams(searchParams) {
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(String(searchParams || ''));
  const statusRaw = (sp.get('status') || 'in_progress').trim().toLowerCase();
  return ACTIVE_SCANS_STATUS_FILTERS.has(statusRaw) ? statusRaw : 'in_progress';
}

export function parseActiveScansTypeFromSearchParams(searchParams) {
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(String(searchParams || ''));
  const typeRaw = (sp.get('type') || 'all').trim().toLowerCase();
  return ACTIVE_SCANS_TYPE_FILTERS.has(typeRaw) ? typeRaw : 'all';
}

/** Build `/app/scans` with optional status/tab filters (survives per-route DashboardPage remounts). */
export function buildScansPathWithFilter({ status = 'all', tab = null, view = 'runs', location = null } = {}) {
  const params = new URLSearchParams();
  const safeStatus = String(status || 'all').trim().toLowerCase();
  const safeTab = String(tab || '').trim().toLowerCase();
  const safeView = String(view || 'runs').trim().toLowerCase();
  const safeLocation = String(location || '').trim().toLowerCase();
  if (safeStatus && safeStatus !== 'all' && SCANS_STATUS_FILTERS.has(safeStatus)) {
    params.set('status', safeStatus);
  }
  if (safeTab === 'web' || safeTab === 'network') {
    params.set('tab', safeTab);
  }
  if (safeView === 'schedules' || safeView === 'in_progress') {
    params.set('view', safeView);
  }
  if (safeLocation && safeLocation !== 'all' && SCANS_LOCATIONS.has(safeLocation)) {
    // Current / Scheduled location tabs removed — normalize legacy aliases to onprem.
    const normalizedLocation = (
      safeLocation === 'current'
      || safeLocation === 'in_progress'
      || safeLocation === 'scheduled'
    )
      ? 'onprem'
      : safeLocation;
    params.set('location', normalizedLocation);
  }
  const qs = params.toString();
  return `${PAGE_TO_PATH.scans}${qs ? `?${qs}` : ''}`;
}

export function parseScansFilterFromSearchParams(searchParams) {
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(String(searchParams || ''));
  const statusRaw = (sp.get('status') || 'all').trim().toLowerCase();
  const tabRaw = (sp.get('tab') || '').trim().toLowerCase();
  const viewRaw = (sp.get('view') || 'runs').trim().toLowerCase();
  const locationRaw = (sp.get('location') || 'all').trim().toLowerCase();
  const normalizedLocation = (
    locationRaw === 'current'
    || locationRaw === 'in_progress'
    || locationRaw === 'scheduled'
  )
    ? 'onprem'
    : locationRaw;
  return {
    status: SCANS_STATUS_FILTERS.has(statusRaw) ? statusRaw : 'all',
    tab: tabRaw === 'web' || tabRaw === 'network' ? tabRaw : null,
    view: SCANS_RECORD_VIEWS.has(viewRaw) ? viewRaw : 'runs',
    location: SCANS_LOCATIONS.has(locationRaw) ? normalizedLocation : 'all',
  };
}
